import React, { useEffect, useState } from "react";
import { 
  Button, 
  CircularProgress, 
  Alert, 
  Box, 
  Typography,
  Divider,
  Grid,
  Card,
  CardContent,
  FormControlLabel,
  Switch
} from "@mui/material";
import SaveIcon from '@mui/icons-material/Save';
import LabelInput from "../../../components/labelInput/LabelInput";
import ClinicFrameConfiguracaoService from "../../../core/http/service/ClinicFrameConfiguracaoService";
import useResponsiveInfo from "../../../hooks/useResponsiveInfo";
import Colors from "../../../constants/Colors";

interface Props {
  empresaId: string;
  isViewState?: boolean;
}

const ClinicFrameGeneralConfig = ({ empresaId, isViewState = false }: Props) => {
  const { isMobile } = useResponsiveInfo();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "success" as "success" | "error" });
  const [config, setConfig] = useState<any>({
    empresaId,
    url: "",
    apiUrl: "",
    urlParamsReceituario: "p=H",
    urlParamsAtendimento: "p=M,H,E,D",
    clientKey: "",
    clientName: "",
    clientCnpj: "",
    clientCnes: "",
    speciality: "Consulta",
    serviceCode: "CONSULTA",
    healthInsurance: "Saude Plena",
    healthInsuranceType: "",
    boardType: "CRM",
    ativo: true,
  });

  useEffect(() => {
    fetchData();
  }, [empresaId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response: any = await ClinicFrameConfiguracaoService.getConfiguracao(empresaId);
      if (response.data && response.data.data) {
        setConfig(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching Clinic Frame config", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ text: "", type: "success" });
    try {
      await ClinicFrameConfiguracaoService.upsertConfiguracao(config);
      setMessage({ text: "Configuração salva com sucesso!", type: "success" });
    } catch (error) {
      setMessage({ text: "Erro ao salvar configuração.", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      {message.text && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage({ ...message, text: "" })}>
          {message.text}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" color="primary" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
                Parâmetros de URL e API
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <LabelInput
                        fullWidth
                        disabled={isViewState}
                        label="Clinic Frame URL (Base)"
                        value={config.url}
                        onChangeText={(e: any) => setConfig({ ...config, url: e.target.value })}
                        placeholder="https://clinicframe.com.br"
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <LabelInput
                        fullWidth
                        disabled={isViewState}
                        label="Clinic Frame API URL"
                        value={config.apiUrl}
                        onChangeText={(e: any) => setConfig({ ...config, apiUrl: e.target.value })}
                        placeholder="https://api.clinicframe.com.br/v1/appointments"
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <LabelInput
                        fullWidth
                        disabled={isViewState}
                        label="Parâmetros URL Receituário"
                        value={config.urlParamsReceituario}
                        onChangeText={(e: any) => setConfig({ ...config, urlParamsReceituario: e.target.value })}
                        placeholder="p=H"
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <LabelInput
                        fullWidth
                        disabled={isViewState}
                        label="Parâmetros URL Atendimento"
                        value={config.urlParamsAtendimento}
                        onChangeText={(e: any) => setConfig({ ...config, urlParamsAtendimento: e.target.value })}
                        placeholder="p=M,H,E,D"
                    />
                </Grid>
              </Grid>

              <Divider sx={{ my: 4 }} />

              <Typography variant="h6" color="primary" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
                Dados do Cliente (Client)
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <LabelInput
                        fullWidth
                        disabled={isViewState}
                        label="Chave do Cliente (Key)"
                        value={config.clientKey}
                        onChangeText={(e: any) => setConfig({ ...config, clientKey: e.target.value })}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <LabelInput
                        fullWidth
                        disabled={isViewState}
                        label="Nome do Cliente"
                        value={config.clientName}
                        onChangeText={(e: any) => setConfig({ ...config, clientName: e.target.value })}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <LabelInput
                        fullWidth
                        disabled={isViewState}
                        label="CNPJ do Cliente"
                        value={config.clientCnpj}
                        onChangeText={(e: any) => setConfig({ ...config, clientCnpj: e.target.value })}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <LabelInput
                        fullWidth
                        disabled={isViewState}
                        label="CNES do Cliente"
                        value={config.clientCnes}
                        onChangeText={(e: any) => setConfig({ ...config, clientCnes: e.target.value })}
                    />
                </Grid>
              </Grid>

              <Divider sx={{ my: 4 }} />

              <Typography variant="h6" color="primary" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
                Configurações do Atendimento
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                    <LabelInput
                        fullWidth
                        disabled={isViewState}
                        label="Especialidade Padrão"
                        value={config.speciality}
                        onChangeText={(e: any) => setConfig({ ...config, speciality: e.target.value })}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <LabelInput
                        fullWidth
                        disabled={isViewState}
                        label="Código do Serviço"
                        value={config.serviceCode}
                        onChangeText={(e: any) => setConfig({ ...config, serviceCode: e.target.value })}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <LabelInput
                        fullWidth
                        disabled={isViewState}
                        label="Conselho Profissional (Board Type)"
                        value={config.boardType}
                        onChangeText={(e: any) => setConfig({ ...config, boardType: e.target.value })}
                        placeholder="CRM"
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <LabelInput
                        fullWidth
                        disabled={isViewState}
                        label="Convênio Padrão"
                        value={config.healthInsurance}
                        onChangeText={(e: any) => setConfig({ ...config, healthInsurance: e.target.value })}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <LabelInput
                        fullWidth
                        disabled={isViewState}
                        label="Tipo de Convênio"
                        value={config.healthInsuranceType}
                        onChangeText={(e: any) => setConfig({ ...config, healthInsuranceType: e.target.value })}
                    />
                </Grid>
                <Grid item xs={12}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={config.ativo}
                                disabled={isViewState}
                                onChange={(e) => setConfig({ ...config, ativo: e.target.checked })}
                                color="primary"
                            />
                        }
                        label={config.ativo ? "Integração Habilitada" : "Integração Desabilitada"}
                    />
                </Grid>
              </Grid>

              {!isViewState && (
                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button 
                      variant="contained" 
                      size="large"
                      startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />} 
                      onClick={handleSave} 
                      disabled={saving}
                      sx={{ borderRadius: 2, px: 4 }}
                  >
                      {saving ? "Salvando..." : "Salvar Alterações"}
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ClinicFrameGeneralConfig;
